import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useRedirect,
  useRefresh,
  SelectInput,
  CreateProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography } from "@material-ui/core";
import { CcaAcademicYearResources, GetParam } from "utils/tools";
import CustomCreateToolbar from "components/CustomCreateToolbar";
import moment from "moment";
import { useField } from "react-final-form";
import CustomDatePickerInput from "components/CustomDatePickerInput";
import CcaTbfCodeInput from "./components/CcaTbfCodeInput";
import { validateCcaRateChartCreation } from "./validation";
import { ccaRateChartType } from "./const";
import CcaRateChartTypeInput from "./components/CcaRateChartTypeInput";
import CustomDateRangeInput from "../../components/CustomDateRangeInput";
import CreateWrapper from "../../components/CreateWrapper";

interface ICustomReferenceInput {
  schoolId: string;
  callback: (x: string) => void;
}

const CustomReferenceInput = (props: ICustomReferenceInput) => {
  const startedAt = useField("startedAt");
  const endedAt = useField("endedAt");
  const invoiceIssuedAt = useField("invoiceIssuedAt");

  return (
    <ReferenceInput
      label="Select CCA Academic Year"
      source="ccaAcademicYearId"
      reference="ccaAcademicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(props.schoolId) }}
      onChange={({ target }) => {
        props.callback(target.value);

        startedAt.input.onChange("");
        endedAt.input.onChange("");
        invoiceIssuedAt.input.onChange("");
      }}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const RateChartCreate = (props: CreateProps) => {
  const [ccaRateCharts, setCcaRateCharts] = useState<ICcaRateChart[]>([]);
  const [idCcaAcademicYear, setIdCcaAcademicYear] = useState<number>();

  const provider = useDataProvider();
  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    { title: "Pricing", link: `/schools/${param.get("schoolId")}/show/5` },
    { title: "Create", link: "" },
  ];

  const transform = (data: any) => {
    switch (data.ccaRateChartType) {
      case ccaRateChartType.costPerTripAndStudentType:
        data.ccaRateChartCostPerTrip = {
          riderPrice: (Number(data.riderPrice) * 100).toFixed(0),
          nonRiderPrice: (Number(data.nonRiderPrice) * 100).toFixed(0),
        };
        break;
      case ccaRateChartType.costPerTripBasedOnArea:
        data.ccaRateChartAreaItems = [...data.itemsToSend];
        break;
      case ccaRateChartType.costPerWeek:
        data.ccaRateChartCostPerWeek = {
          price: (Number(data.price) * 100).toFixed(0),
        };
        break;
    }

    data.status = "active";

    delete data.riderPrice;
    delete data.nonRiderPrice;
    delete data.itemsToSend;
    delete data.price;

    return data;
  };

  const CustomRedirect = (props: { data: IRateChart }) => {
    redirect(`/schools/${props.data.schoolId}/show/5`);
    refresh();
  };

  const ccaAcademicYearResources = CcaAcademicYearResources(idCcaAcademicYear);

  useEffect(() => {
    if (idCcaAcademicYear !== undefined) {
      provider
        .getList("ccaRateCharts", {
          filter: { CcaAcademicYearId: Number(idCcaAcademicYear) },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then((result) => {
          setCcaRateCharts(result.data as ICcaRateChart[]);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCcaAcademicYear]);

  const ccaAcademicYearInputCallback = (id: any) => {
    setIdCcaAcademicYear(id);
  };

  let excludeDates: IExcludeDates[] = [];
  ccaRateCharts.forEach((ccaRateChart: ICcaRateChart) => {
    let temp: IExcludeDates = {
      startDate: ccaRateChart.startedAt,
      endDate: ccaRateChart.endedAt,
    };

    excludeDates.push(temp);
  });

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New CCA Rate Chart"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm
          validate={(values: any) =>
            validateCcaRateChartCreation(values, excludeDates)
          }
          toolbar={<CustomCreateToolbar />}
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School & CCA Academic Year</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomReferenceInput
                callback={ccaAcademicYearInputCallback}
                schoolId={param.get("schoolId")}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CcaTbfCodeInput />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CustomDateRangeInput
                fromSource="startedAt"
                toSource="endedAt"
                fromLabel="Pricing Start Date"
                toLabel="Pricing End Date"
                parentStartDate={moment
                  .utc(ccaAcademicYearResources?.startDate)
                  .format("yyyy-MM-DD")}
                fromMaxDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                toMaxDate={moment
                  .utc(ccaAcademicYearResources?.endDate)
                  .format("yyyy-MM-DD")}
                excludeDates={excludeDates}
                gridItemWidth={3}
              />
            </Grid>

            <Grid item xs={3}>
              <CustomDatePickerInput
                name="invoiceIssuedAt"
                label="Invoice Issue Date *"
                min={moment().format("Y-MM-DD")}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <CcaRateChartTypeInput schoolId={Number(param.get("schoolId"))} />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default RateChartCreate;
